<template>
  <div class="flex flex-col lg:h-0 lg:min-h-full">
    <div class="flex-none px-2">
      <h5 class="text-sm font-medium text-gray-600 mb-2">Tags</h5>
    </div>
    <div class="flex-1 lg:h-0">
      <div class="h-full overflow-auto fancy-scrollbar px-2">
        <div>
          <ValidationObserver ref="observer">
            <template #default="{ handleSubmit, invalid }">
              <form
                class="bg-white px-4 pb-4 rounded border border-gray-400"
                @submit.prevent="handleSubmit(onSubmit)"
              >
                <BaseLayoutSpaced>
                  <ul>
                    <template v-for="category in categories">
                      <li :key="category.id" class="border-b border-gray-200 -mx-4 px-4 pt-3 pb-4">
                        <BaseFormLabel>
                          {{ category.name }}
                        </BaseFormLabel>
                        <ul class="-m-1 flex flex-wrap">
                          <template v-for="(accountTag, accountTagIdx) in accountTags">
                            <li
                              :key="accountTag.id"
                              v-if="tagHasCategory(accountTag, category)"
                              class="p-1"
                            >
                              <ValidationProvider
                                :ref="`tags.${accountTagIdx}`"
                                :vid="`tags.${accountTagIdx}`"
                                :name="`tags[${accountTagIdx}]`"
                                slim
                              >
                                <template>
                                  <label class="relative">
                                    <input
                                      type="checkbox"
                                      v-model="tagsInput"
                                      :value="accountTag.id"
                                      :disabled="updating"
                                      class="opacity-0 absolute checked-next-sibling:bg-gray-900 checked-next-sibling:text-white disabled-next-sibling:bg-gray-100 disabled-next-sibling:text-gray-600 invalid-next-sibling:bg-red-500"
                                      @click="$event.target.setCustomValidity('')"
                                    />
                                    <span
                                      class="inline-block px-4 py-1 rounded-full bg-gray-300 select-none transition-color transition-faster"
                                    >
                                      {{ accountTag.name }}
                                    </span>
                                  </label>
                                </template>
                              </ValidationProvider>
                            </li>
                          </template>
                        </ul>
                      </li>
                    </template>
                  </ul>
                  <BaseFormInlineMessage v-if="errorMessage" danger>
                    {{ errorMessage }}
                  </BaseFormInlineMessage>
                  <div>
                    <BaseButton type="submit" primary :disabled="updating || invalid">
                      Update
                    </BaseButton>
                  </div>
                </BaseLayoutSpaced>
              </form>
            </template>
          </ValidationObserver>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { uniqBy } from 'lodash-es';
import { HTTP_UNPROCESSABLE_ENTITY } from '@/api/http-response-codes';
import veeValidateErrorsMixin from '@/mixins/veeValidateErrorsMixin';

export default {
  mixins: [veeValidateErrorsMixin],

  props: {
    contactRequest: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loading: false,
      updating: false,
      data: {
        tags: [],
      },
      errorMessage: '',
    };
  },

  computed: {
    accountTags() {
      return this.$store.getters['ui/accountTags/itemsByParent'](this.contactRequest.accountId);
    },
    categories() {
      return uniqBy(this.accountTags.map((tag) => tag?.tagCategoryId))
        .map((tagCategoryId) => this.$store.state.entities.tagCategories.byId[tagCategoryId])
        .filter(Boolean);
    },
    tagsInput: {
      get() {
        return this.data.tags;
      },
      set(tags) {
        this.data.tags = tags;
      },
    },
  },

  mounted() {
    this.onMounted();
  },

  destroyed() {
    this.onDestroyed();
  },

  methods: {
    tagHasCategory(tag, category) {
      return tag?.tagCategoryId === category.id;
    },
    async onMounted() {
      try {
        this.loading = true;

        await Promise.all([this.loadAccountTags(), this.loadContactRequestTags()]);
      } catch (error) {
        /** ignore */
      } finally {
        this.loading = false;
      }
    },
    async onDestroyed() {
      const { id, accountId } = this.contactRequest;

      await this.$store.dispatch('ui/contactRequestTags/clearByParent', {
        parentId: id,
      });

      await this.$store.dispatch('ui/accountTags/clearByParent', {
        parentId: accountId,
      });
    },
    async loadAccountTags() {
      const { accountId } = this.contactRequest;

      try {
        await this.$store.dispatch('ui/accountTags/load', {
          accountId,
          params: {
            filter: {
              taggable_by: 'contact_requests',
            },
            include: 'tagCategory',
            page_size: 500,
          },
        });

        // this.data.tags = this.contactRequest.tags;
      } catch (error) {
        /** ignore */
      }
    },
    async loadContactRequestTags() {
      const { id } = this.contactRequest;

      try {
        const { tagIds = [] } = await this.$store.dispatch('ui/contactRequestTags/load', {
          id,
        });

        this.data.tags = [...tagIds];
      } catch (error) {
        /** ignore */
      }
    },
    async onSubmit() {
      const { id } = this.contactRequest;
      this.errorMessage = '';
      this.updating = true;

      try {
        await this.$store.dispatch('ui/contactRequestTags/update', {
          id,
          formData: this.data,
        });
      } catch (response) {
        const { data: { message = '', errors = {} } = {}, status = null } = response || {};
        this.errorMessage = message;
        if (status === HTTP_UNPROCESSABLE_ENTITY) {
          this.applyServerErrors(errors);
        }
      } finally {
        this.updating = false;
      }
    },
    applyServerErrors(errors) {
      this.observer = this.$refs.observer;
      this.addServerErrors(errors);
      Object.keys(errors).forEach((key) => {
        const input = this.$el.querySelector(`input[name="${key}"]`);

        if (input) {
          input.setCustomValidity(errors[key]);
        }
      });
    },
  },
};
</script>
